import { FolderFilled, MoreOutlined } from "@ant-design/icons"
import { Button, Card, Dropdown, Flex, Tag, Typography } from "antd"
import type { MenuItemType } from "antd/es/menu/interface"
import React, { memo, useState } from "react"
import { useTranslation } from "react-i18next"

import { type FolderListItem } from "../types/folder.types.ts"
import { FILE_DRAG_MIME } from "./FileCard.tsx"

const { Text } = Typography

export const FOLDER_DRAG_MIME = "application/x-ezbuild-folder-id"

interface FolderCardProps {
  folder: FolderListItem
  onOpen: (folder: FolderListItem) => void
  actions: MenuItemType[]
  draggable?: boolean
  onDropFile?: (fileKey: string, folder: FolderListItem) => void
  onDropFolder?: (folderId: string, folder: FolderListItem) => void
}

export const FolderCard = memo(
  ({
    folder,
    onOpen,
    actions,
    draggable = false,
    onDropFile,
    onDropFolder
  }: FolderCardProps) => {
    const { t } = useTranslation()
    const [menuOpen, setMenuOpen] = useState(false)
    const [dragOver, setDragOver] = useState(false)

    const canDrop = !!onDropFile || !!onDropFolder

    return (
      <Card
        hoverable
        size="small"
        draggable={draggable}
        onDragStart={(e) => {
          e.dataTransfer.setData(FOLDER_DRAG_MIME, folder.id)
          e.dataTransfer.effectAllowed = "move"
        }}
        className={`relative ${dragOver ? "ring-2 ring-primary bg-primary/5" : ""}`}
        onClick={() => onOpen(folder)}
        onDragOver={(e) => {
          if (!canDrop) return
          e.preventDefault()
          setDragOver(true)
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          if (!canDrop) return
          e.preventDefault()
          setDragOver(false)
          const fileKey = e.dataTransfer.getData(FILE_DRAG_MIME)
          const folderId = e.dataTransfer.getData(FOLDER_DRAG_MIME)
          if (fileKey && onDropFile) onDropFile(fileKey, folder)
          else if (folderId && folderId !== folder.id && onDropFolder) {
            onDropFolder(folderId, folder)
          }
        }}
      >
        <Flex align="center" gap="small">
          <FolderFilled className="text-3xl text-warning shrink-0" />
          <Flex vertical className="min-w-0 flex-1">
            <Flex align="center" gap={4}>
              <Text ellipsis={{ tooltip: folder.name }} strong className="text-sm">
                {folder.name}
              </Text>
              {folder.created_by_is_customer && (
                <Tag color="blue" className="!m-0 !text-xs shrink-0">
                  {folder.created_by_name
                    ? t("files.filters.sharedByCustomerNamed", {
                        name: folder.created_by_name
                      })
                    : t("files.filters.sharedByCustomer")}
                </Tag>
              )}
            </Flex>
            <Text type="secondary" className="text-xs">
              {t("files.folder.itemCount", {
                count: folder.files_count + folder.children_count
              })}
            </Text>
          </Flex>
          {actions.length > 0 && (
            <div onClick={(e) => e.stopPropagation()}>
              <Dropdown
                trigger={["click"]}
                open={menuOpen}
                onOpenChange={setMenuOpen}
                menu={{ items: actions }}
              >
                <Button type="text" size="small" icon={<MoreOutlined />} />
              </Dropdown>
            </div>
          )}
        </Flex>
      </Card>
    )
  }
)

FolderCard.displayName = "FolderCard"
